import {
    Avatar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    FormHelperText,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Stack,
} from '@mui/material';
import React from 'react';
import { ModalProps } from '../../../types/modal';
import {
    useCreateReservationChatMutation,
    useGetAllReservationsQuery,
    useGetIndividualOwnerAssetsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import UserRole from '../../../../shared/AuthRoles';
import { FormValues as Experience } from '../../../types/experience';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Close, FilterList, Search } from '@mui/icons-material';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { ReservationChat } from '../../../../app/Slices/SevenDayApiTypes';
import toast from '../../../shared/Toast';
import FormTextField from '../../../shared/FormTextField';
import { useNavigate } from 'react-router';
import { useChatContext } from 'stream-chat-react';

const Schema = z.object({
    experience: z.number().optional(),
    bookingId: z.number(),
    message: z.string().min(1, { message: 'Required' }),
});

const MessageComposer = ({ open, onClose }: ModalProps) => {
    const { setActiveChannel, client } = useChatContext();

    const loggedInUser = UserRole();
    const navigate = useNavigate();

    const [createChat] = useCreateReservationChatMutation();

    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        resolver: zodResolver(Schema),
    });

    const {
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const { data: experiences } = useGetIndividualOwnerAssetsQuery(
        {
            url: `/owners/${loggedInUser.company}/modules`,
            status: 'published',
        },
        { skip: !loggedInUser?.role.includes('owner') },
    );
    const { data: reservations } = useGetAllReservationsQuery({
        bookerType: 'guest',
    });

    const selectedExperience = useWatch({ name: 'experience', control });

    const filteredReservations = reservations?.filter(
        (reservation: any) =>
            !selectedExperience ||
            reservation.module?.id === selectedExperience,
    );

    const onSubmit = async (data: FormValues) => {
        const request: ReservationChat = {
            ...data,
            isPrivate: false,
        };

        try {
            const response = await createChat(request).unwrap();

            const channel = client.getChannelById(
                'messaging',
                response.channelId,
                {},
            );
            navigate(`/messages/${response.channelId}`);
            setActiveChannel(channel);
        } catch (error) {
            toast.error('Error sending message');
        }
    };

    const formatDate = (input: string) => {
        const date = new Date(input);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        New Message
                        <IconButton
                            onClick={onClose}
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={1}>
                            <div>
                                <InputLabel>Experience</InputLabel>
                                <FormAutocomplete
                                    name='experience'
                                    control={control}
                                    options={
                                        experiences?.map(
                                            (experience: Experience) => ({
                                                value: experience.id,
                                                label: experience.name,
                                                ...experience,
                                            }),
                                        ) || []
                                    }
                                    disableClearable={undefined}
                                    startAdornment={
                                        <FilterList
                                            color='disabled'
                                            sx={{ pl: 0.5 }}
                                        />
                                    }
                                    renderOption={(
                                        props,
                                        option: {
                                            value: any;
                                            label: string;
                                        } & Experience,
                                    ) => (
                                        <li key={option.id} {...props}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    variant='rounded'
                                                    src={
                                                        option.photos?.[0]?.src
                                                    }
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={option.name}
                                                secondary={`${option.streetAddress}, ${option.suburb}`}
                                            />
                                        </li>
                                    )}
                                />
                            </div>
                            <div>
                                <InputLabel required>Reservation</InputLabel>
                                <FormAutocomplete
                                    name='bookingId'
                                    control={control}
                                    options={
                                        filteredReservations?.map(
                                            (reservation: any) => ({
                                                value: reservation.id,
                                                label: `Reservation #${reservation.id}`,
                                                ...reservation,
                                            }),
                                        ) || []
                                    }
                                    disableClearable={undefined}
                                    startAdornment={
                                        <Search
                                            color='disabled'
                                            sx={{ pl: 0.5 }}
                                        />
                                    }
                                    renderOption={(props, option: any) => (
                                        <li key={option.id} {...props}>
                                            <ListItemText
                                                primary={`Reservation #${option.id}`}
                                                secondary={`${formatDate(
                                                    option.startDate,
                                                )} - ${formatDate(
                                                    option.endDate,
                                                )}
                                                `}
                                            />
                                        </li>
                                    )}
                                />
                            </div>
                            <div>
                                <InputLabel required>Message</InputLabel>
                                <FormTextField
                                    name='message'
                                    placeholder='Hi! Enjoy your stay at...'
                                    control={control}
                                    multiline
                                    rows={4}
                                />
                                <FormHelperText>
                                    If a channel exists for this reservation,
                                    this message will be sent there—otherwise, a
                                    new channel will be created.
                                </FormHelperText>
                            </div>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <CustomButton
                            color='primary'
                            variant='outlined'
                            onClick={onClose}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            color='primary'
                            variant='contained'
                            type='submit'
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            Send
                        </CustomButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default MessageComposer;
